import React from 'react';
import { Bot, X, Calendar, User, Clock } from 'lucide-react';

interface CalendarBot {
  source: 'calendar' | 'manual';
  botSessionId: string;
  botId: string;
  eventTitle: string;
  meetingUrl: string;
  startTime: string;
  status: 'scheduled' | 'creating' | 'joining' | 'active' | 'failed' | 'terminated';
  platform: 'meet' | 'zoom' | 'teams';
  websocketUrl?: string;
  isSalespersonHost?: boolean;
}

interface ResumeSessionModalProps {
  isOpen: boolean;
  bots: CalendarBot[];
  onResume: (bot: CalendarBot) => void | Promise<void>;
  onDismiss: () => void;
}

export const ResumeSessionModal: React.FC<ResumeSessionModalProps> = ({
  isOpen,
  bots,
  onResume,
  onDismiss,
}) => {
  if (!isOpen || bots.length === 0) {
    return null;
  }

  const formatTime = (isoString: string) => {
    try {
      const date = new Date(isoString);
      return date.toLocaleTimeString('en-US', {
        hour: 'numeric',
        minute: '2-digit',
        hour12: true
      });
    } catch {
      return '';
    }
  };

  const getPlatformIcon = (platform: string) => {
    switch (platform.toLowerCase()) {
      case 'meet':
        return '📹';
      case 'zoom':
        return '🎥';
      case 'teams':
        return '👥';
      default:
        return '🔗';
    }
  };

  const getSourceIcon = (source: 'calendar' | 'manual') => {
    return source === 'calendar' ? <Calendar className="w-4 h-4" /> : <User className="w-4 h-4" />;
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full max-h-[80vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 text-white p-6 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Bot className="w-6 h-6" />
            <div>
              <h2 className="text-xl font-bold">Resume Active Session</h2>
              <p className="text-sm text-blue-100 mt-1">
                You have {bots.length} active {bots.length === 1 ? 'session' : 'sessions'} in progress
              </p>
            </div>
          </div>
          <button
            onClick={onDismiss}
            className="text-white hover:text-blue-200 transition-colors"
            aria-label="Close modal"
          >
            <X className="w-6 h-6" />
          </button>
        </div>

        {/* Bot List */}
        <div className="flex-1 overflow-y-auto p-6 space-y-3">
          {bots.map((bot) => (
            <div
              key={bot.botId}
              className="border border-gray-200 rounded-lg p-4 hover:border-blue-300 hover:bg-blue-50 transition-all cursor-pointer"
              onClick={() => onResume(bot)}
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  {/* Bot Title */}
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl">{getPlatformIcon(bot.platform)}</span>
                    <h3 className="font-semibold text-gray-900">{bot.eventTitle}</h3>
                  </div>

                  {/* Bot Details */}
                  <div className="space-y-2 text-sm text-gray-600">
                    <div className="flex items-center gap-2">
                      {getSourceIcon(bot.source)}
                      <span className="capitalize">{bot.source} Bot</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Clock className="w-4 h-4" />
                      <span>Started at {formatTime(bot.startTime)}</span>
                    </div>

                    <div className="flex items-center gap-2">
                      <Bot className="w-4 h-4" />
                      <span className="font-mono text-xs bg-gray-100 px-2 py-1 rounded">
                        {bot.botId}
                      </span>
                    </div>

                    <div className="flex items-center gap-2">
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        bot.status === 'active'
                          ? 'bg-green-100 text-green-700'
                          : 'bg-yellow-100 text-yellow-700'
                      }`}>
                        {bot.status}
                      </span>
                      <span className={`px-2 py-1 rounded-full text-xs font-medium ${
                        bot.isSalespersonHost
                          ? 'bg-blue-100 text-blue-700'
                          : 'bg-purple-100 text-purple-700'
                      }`}>
                        {bot.isSalespersonHost ? 'You are Host' : 'You are Participant'}
                      </span>
                    </div>
                  </div>
                </div>

                {/* Resume Button */}
                <button
                  className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium text-sm whitespace-nowrap"
                  onClick={(e) => {
                    e.stopPropagation();
                    onResume(bot);
                  }}
                >
                  Resume
                </button>
              </div>
            </div>
          ))}
        </div>

        {/* Footer */}
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="flex items-center justify-between">
            <p className="text-sm text-gray-600">
              Select a session to resume or dismiss to start a new session
            </p>
            <button
              onClick={onDismiss}
              className="px-4 py-2 text-gray-700 hover:text-gray-900 font-medium text-sm"
            >
              Start New Session
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};
